import { round } from "common/helpers/Math";
import { Nullable } from "common/records";
import { EnhancedPricePresentationConfigRecord } from "common/records/EnhancedPricePresentationConfig";
import { RootActions, RootState } from "common/reducers";
import {
  ADD_DISCOUNT_GROUP,
  RESET_EDIT_DISCOUNT_FORM,
  RESET_PRICE_PRESENTATION,
  SET_CURRENT_SLIDE_INDEX,
  SET_SELECTED_ESTIMATE_ID,
  SET_SLIDE_ESTIMATE_IDS,
  UPDATE_FINANCING,
  UPDATE_PRICE_PRESENTATION,
} from "common/reducers/pricePresentation.actions";
import { currentOrgPrefConfig } from "common/selectors/org.selectors";
import { List, Map, Record } from "immutable";

export const FinancingRecord = Record({
  downPayment: 0,
  maxPayment: 0,
  minPayment: 0,
});

/** Map<estimateId, discountUuid> */
export type DiscountGroupMap = Map<number, string>;

export const PricePresentationStateRecord = Record({
  currentSlideIndex: 0,
  editDiscountForm: Map<string, any>(),
  estimateDiscountGroups: List<DiscountGroupMap>(),
  financing: FinancingRecord(),
  selectedEstimateId: null as Nullable<number>,
  showExactValues: false,
  slideEstimateIds: List<number>(),
});

export const pricePresentationInitialState = PricePresentationStateRecord();
export type PricePresentationState = typeof pricePresentationInitialState;
export type IFinancingRecord = typeof pricePresentationInitialState.financing;

export const model = "pricePresentation";
export const pricePresentationReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, pricePresentationInitialState);
  }

  switch (action.type) {
    case ADD_DISCOUNT_GROUP: {
      const { group } = action.payload;
      return state.updateIn([model, "estimateDiscountGroups"], (groups: List<DiscountGroupMap>) => groups.push(group));
    }

    case RESET_EDIT_DISCOUNT_FORM: {
      return state.setIn([model, "editDiscountForm"], Map<string, any>());
    }

    case RESET_PRICE_PRESENTATION: {
      return state.set(model, pricePresentationInitialState);
    }

    case SET_CURRENT_SLIDE_INDEX: {
      const { index } = action.payload;
      return state.setIn([model, "currentSlideIndex"], index);
    }

    case SET_SELECTED_ESTIMATE_ID: {
      const { estimateId } = action.payload;
      return state.setIn([model, "selectedEstimateId"], estimateId);
    }

    case SET_SLIDE_ESTIMATE_IDS: {
      const { ids } = action.payload;
      return state.setIn([model, "slideEstimateIds"], ids);
    }

    case UPDATE_FINANCING: {
      const { total } = action.payload;

      const downPayment = state.getIn([model, "financing"]).get("downPayment");
      const newTotal = total - downPayment;
      const config: Nullable<EnhancedPricePresentationConfigRecord> = currentOrgPrefConfig(state, {
        path: ["enhanced_price_presentation"],
      });
      const minPaymentRange = config?.get("min_payment_range") || 0;
      const maxPaymentRange = config?.get("max_payment_range") || 0;

      const minPayment = round(newTotal * (minPaymentRange / 100));
      const maxPayment = round(newTotal * (maxPaymentRange / 100));

      return state
        .setIn([model, "financing", "minPayment"], minPayment)
        .setIn([model, "financing", "maxPayment"], maxPayment);
    }

    case UPDATE_PRICE_PRESENTATION: {
      const { rootPath, name, value } = action.payload.event;
      return state.setIn(rootPath.concat(name.split(".")), value);
    }

    default:
      return state;
  }
};
